/**
 * Two-language UI (English and Danish). Dictionaries are flat key/value maps;
 * `da` is typed against `en`, so a missing Danish string is a compile error.
 * The chosen language is remembered per browser and sent to the API as
 * Accept-Language, so validation messages come back in the same language.
 */
import { DEFAULT_LOCALE, LOCALES, type Locale } from "@clinic/shared";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { da } from "./locales/da";
import { en } from "./locales/en";

export type MessageKey = keyof typeof en;
const dictionaries: Record<Locale, Record<MessageKey, string>> = { en, da };

const STORAGE_KEY = "cb_locale";

function isLocale(v: unknown): v is Locale {
  return typeof v === "string" && (LOCALES as readonly string[]).includes(v);
}

/** The visitor's explicit choice, if they made one. */
export function storedLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null; // Private mode or blocked storage.
  }
}

/** A language named in the URL (`?lang=da`), as used by the language-specific URLs search engines index. */
function urlLocale(): Locale | null {
  if (typeof window === "undefined") return null;
  const lang = new URLSearchParams(window.location.search).get("lang");
  return isLocale(lang) ? lang : null;
}

/** The URL's language, else the stored choice, else the browser's language, else English. */
export function detectLocale(): Locale {
  const fromUrl = urlLocale();
  if (fromUrl) return fromUrl;
  const stored = storedLocale();
  if (stored) return stored;
  const nav = typeof navigator !== "undefined" ? navigator.language.toLowerCase().split("-")[0] : undefined;
  return isLocale(nav) ? nav : DEFAULT_LOCALE;
}

let activeLocale: Locale = DEFAULT_LOCALE;
/** The current language outside React (the API client reads it for Accept-Language). */
export const getLocale = () => activeLocale;

export function translate(locale: Locale, key: MessageKey, vars?: Record<string, string | number>): string {
  const template = dictionaries[locale][key] ?? dictionaries.en[key] ?? key;
  return vars ? template.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? `{${name}}`)) : template;
}

/** Translate an API error by its stable code, falling back to the server's message. */
export function translateError(locale: Locale, err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  const key = code ? (`errors.${code}` as MessageKey) : undefined;
  if (key && key in dictionaries.en) return translate(locale, key);
  if (err instanceof Error && err.message) return err.message;
  return translate(locale, "common.error_generic");
}

interface LocaleContextValue {
  locale: Locale;
  /** `persist: false` applies a page default (e.g. a clinic's language) without overriding the visitor's choice. */
  setLocale: (l: Locale, persist?: boolean) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  tError: (err: unknown) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children, initial }: { children: ReactNode; initial?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => initial ?? detectLocale());
  const persistNext = useRef(false);
  activeLocale = locale;

  useEffect(() => {
    activeLocale = locale;
    document.documentElement.lang = locale;
    // Keep a language-specific URL in step with the language shown.
    const url = new URL(window.location.href);
    if (url.searchParams.has("lang") && url.searchParams.get("lang") !== locale) {
      url.searchParams.set("lang", locale);
      window.history.replaceState(window.history.state, "", url);
    }
    if (!persistNext.current) return;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Not persisting is fine.
    }
  }, [locale]);

  const setLocale = useCallback((l: Locale, persist = true) => {
    persistNext.current = persist;
    setLocaleState(l);
  }, []);
  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => translate(locale, key, vars),
      tError: (err) => translateError(locale, err),
    }),
    [locale, setLocale],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useT(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useT must be used inside LocaleProvider");
  return ctx;
}

export const intlLocale = (locale: Locale) => (locale === "da" ? "da-DK" : "en-GB");

export function formatMoney(locale: Locale, cents: number, currency: string): string {
  return new Intl.NumberFormat(intlLocale(locale), { style: "currency", currency }).format(cents / 100);
}

export function formatTime(locale: Locale, iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).format(new Date(iso));
}

export function formatDate(
  locale: Locale,
  iso: string,
  timeZone?: string,
  opts: Intl.DateTimeFormatOptions = {},
): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone,
    ...opts,
  }).format(new Date(iso));
}

export function formatDateTime(locale: Locale, iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).format(new Date(iso));
}

/** Local calendar date (YYYY-MM-DD) of an instant in a time zone. */
export function localDate(iso: string | number, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + days)).toISOString().slice(0, 10);
}

/** Switch between the two languages; rendered on every page. `tone="light"` is for dark backgrounds. */
export function LanguageSwitcher({ className = "", tone = "dark" }: { className?: string; tone?: "dark" | "light" }) {
  const { locale, setLocale, t } = useT();
  const light = tone === "light";
  return (
    <fieldset
      className={`inline-flex rounded-lg ring-1 ${light ? "ring-white/30" : "ring-slate-300"} ${className}`}
      aria-label={t("common.language")}
    >
      {LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLocale(l)}
          aria-pressed={locale === l}
          lang={l}
          className={`px-2.5 py-1 text-xs font-semibold uppercase first:rounded-l-lg last:rounded-r-lg ${
            locale === l
              ? light
                ? "bg-white text-navy-900"
                : "bg-slate-800 text-white"
              : light
                ? "text-white/80 hover:bg-white/10"
                : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          {l}
        </button>
      ))}
    </fieldset>
  );
}
